import * as React from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  Clock,
  Loader2,
  Mail,
  Share2,
} from 'lucide-react'
import { toast } from 'sonner'
import { DocumentAcknowledgment } from './-components/DocumentAcknowledgment'
import { QuizLesson } from './-components/QuizLesson'
import { VideoLesson } from './-components/VideoLesson'
import {
  completionRuleLabel,
  lessonTypeLabel,
} from './-components/learning-labels'
import {
  AttachmentList,
  AttachmentsHeading,
} from './-components/AttachmentList'
import {
  chapterMinutes,
  groupLessonsByChapter,
} from './-components/course-chapters'
import { FavoriteButton } from './-components/FavoriteButton'
import { DiscussionPanel } from './-components/DiscussionPanel'
import { TranscriptPanel, formatCueTime } from './-components/TranscriptPanel'
import type { QuizQuestion } from '@/types/learning.types'
import {
  Tabs,
  TabsContent,
  TabsContents,
  TabsList,
  TabsTrigger,
} from '@/components/animate-ui/components/radix/tabs'
import { useCurrentUser } from '@/hooks/repositories/useUsersRepository'
import { useCan } from '@/hooks/permissions/use-can'
import { LEARNING_PERMISSION_KEYS } from '@/lib/permissions/learning-permissions'
import { PageTitle } from '@/components/PageTitle'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Markdown } from '@/components/shared/Markdown'
import { cn } from '@/lib/utils'
import {
  useCompleteLesson,
  useLearnerCourse,
  useLearningTranscript,
  useLearningVideoChapters,
  useReportWatchProgress,
  useStartLesson,
} from '@/hooks/repositories/useLearningRepository'

export const Route = createFileRoute(
  '/_app/learning/$courseSlug/lesson/$lessonId',
)({
  component: RouteComponent,
})

/**
 * The lesson player (sc-1245).
 *
 * Video at the top, content below, and a course progress bar — the layout the
 * story specifies, and the one that makes sense: the thing you came to do
 * first, the context second, and where you are in the whole course always
 * visible so a long course does not feel unbounded.
 *
 * The Finish control is enabled only when the lesson's rule is satisfied, and
 * says what is still missing when it is not. The server checks the same rule
 * again, so this is the honest presentation of it rather than the enforcement.
 */
/**
 * The transcript tab's own component (sc-1294).
 *
 * Split out so the query hook is not called conditionally — the tab only
 * exists for lessons with an uploaded video, and hooks cannot live behind that
 * condition in the page itself.
 */
function LessonTranscript({
  assetId,
  currentTimeMs,
  onSeek,
}: {
  assetId: string
  currentTimeMs: number
  onSeek: (ms: number) => void
}) {
  const { data: transcript, isPending } = useLearningTranscript(assetId)
  return (
    <TranscriptPanel
      transcript={transcript}
      isPending={isPending}
      currentTimeMs={currentTimeMs}
      onSeek={onSeek}
    />
  )
}

function RouteComponent() {
  const { courseSlug, lessonId } = Route.useParams()
  const navigate = useNavigate()

  const { data: course, isPending } = useLearnerCourse(courseSlug)
  const startLesson = useStartLesson()
  const complete = useCompleteLesson(courseSlug)
  const reportProgress = useReportWatchProgress()

  const [watchedPercent, setWatchedPercent] = React.useState(0)
  // Kept alongside the percent so the flush below can send a complete,
  // truthful progress row rather than one with a resume position of zero.
  const watchPositionRef = React.useRef(0)
  // The media element, handed over by MuxVideo when metadata loads — the only
  // way to seek, since the player exposes no ref (sc-1294).
  const playerRef = React.useRef<HTMLVideoElement | null>(null)
  const [currentTimeMs, setCurrentTimeMs] = React.useState(0)
  // Markers for the scrubber (sc-1294). Fetched here rather than inside the
  // video component because hooks cannot live behind the "does this lesson
  // have a video" condition.
  const { data: videoChapters = [] } = useLearningVideoChapters(
    (course?.lessons.find((item) => item.id === lessonId)?.config
      .mediaAssetId as string | undefined) ?? undefined,
  )
  // Above the early returns: hooks must run in the same order every render.
  const [quizPassed, setQuizPassed] = React.useState(false)

  const lesson = course?.lessons.find((item) => item.id === lessonId)
  const index = course?.lessons.findIndex((item) => item.id === lessonId) ?? -1
  const { data: me } = useCurrentUser()
  const { has } = useCan()
  // Pin and remove-anyone's-post are editorial acts, so they follow the author
  // key rather than "is this my course".
  const canModerate = has(LEARNING_PERMISSION_KEYS.author)

  const nextLesson =
    course && index >= 0 ? course.lessons[index + 1] : undefined

  // Opening the lesson is what moves the assignment into "in progress", so it
  // fires once on mount rather than waiting for the learner to do something.
  const startedRef = React.useRef(false)
  React.useEffect(() => {
    if (!lesson || startedRef.current) return
    startedRef.current = true
    startLesson.mutate(lessonId)
  }, [lesson, lessonId])

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!course || !lesson) {
    return (
      <div className="py-4">
        <PageTitle title="Learning" />
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm font-medium">Lesson not found</p>
            <Button asChild variant="outline" size="sm">
              <Link to="/learning">Back to Learning</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const completedCount = course.lessons.filter(
    (item) => item.completedAt,
  ).length
  const coursePercent = Math.round(
    (completedCount / course.lessons.length) * 100,
  )
  const isDone = Boolean(lesson.completedAt)

  // config is Json on the wire, so each type reads what it needs out of it —
  // narrowed here rather than cast at the point of use.
  const bodyMarkdown =
    typeof lesson.config.bodyMarkdown === 'string'
      ? lesson.config.bodyMarkdown
      : null

  const rule = lesson.completionRule
  const hasVideo = Boolean(
    lesson.config.mediaAssetId || lesson.config.externalUrl,
  )
  // Only an uploaded Mux video has a transcript. An external embed is somebody
  // else's player and reports nothing back to us.
  const videoAssetId = lesson.config.mediaAssetId as string | undefined
  const lessonAttachments = course.attachments.filter((attachment) =>
    attachment.lessonIds.includes(lesson.id),
  )
  const threshold = rule.kind === 'WATCH_THRESHOLD' ? rule.percent : null

  // What still stands between the learner and finishing. Null means ready.
  // What the SERVER already credits, versus what this sitting has watched.
  //
  // Local state starts at 0 on every mount, so without this a reload threw
  // away credit the server had already recorded: the video resumed at the
  // right position but the gate read "you are at 0%", and the only way out
  // was to watch the whole thing again. The server takes the max of the two,
  // so the honest number to show is the max as well.
  const creditedPercent = Math.max(watchedPercent, lesson.watchedPercent)

  const blockedReason = (() => {
    if (isDone) return null
    if (rule.kind === 'WATCH_THRESHOLD' && creditedPercent < rule.percent) {
      return `Watch ${rule.percent}% to finish — you are at ${Math.floor(creditedPercent)}%.`
    }
    if (rule.kind === 'QUIZ' && !quizPassed) {
      return `Pass the quiz to finish — ${rule.passPercent}% needed.`
    }
    return null
  })()

  function goNext() {
    if (nextLesson) {
      void navigate({
        to: '/learning/$courseSlug/lesson/$lessonId',
        params: { courseSlug, lessonId: nextLesson.id },
      })
    } else {
      void navigate({ to: '/learning/$courseSlug', params: { courseSlug } })
    }
  }

  return (
    <div className="py-4">
      <PageTitle title={`${course.title} — ${lesson.title}`} />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/learning/$courseSlug" params={{ courseSlug }}>
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          {course.title}
        </Link>
      </Button>

      {/* Course position, always visible. */}
      <div className="mb-5">
        <div className="mb-1.5 flex items-center justify-between text-xs text-muted-foreground">
          <span>
            Lesson {index + 1} of {course.lessons.length}
          </span>
          <span className="tabular-nums">{coursePercent}% complete</span>
        </div>
        <Progress value={coursePercent} />
      </div>

      {/* Two columns from lg up: the lesson itself, and where you are in the
          course. Full width rather than a 3xl column because the video is the
          point of most lessons, and a 768px player in the middle of a 1440px
          screen wastes the thing people came for. */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem] xl:gap-8">
        <div className="min-w-0">
          {/* Video first — the story is explicit that it sits at the top, and
            since VIDEO and MARKDOWN merged, the same lesson can carry the
            write-up underneath rather than splitting it into a second step. */}
          {/* Rounded and clipped. A raw video element ran to the corners of the
            page with a hard black edge against the background, which is what
            made the whole page read as unfinished. */}
          {lesson.type === 'CONTENT' && hasVideo && (
            <div className="mb-4 overflow-hidden rounded-xl border bg-black shadow-sm">
              <VideoLesson
                lessonId={lesson.id}
                lessonTitle={lesson.title}
                mediaAssetId={
                  (lesson.config.mediaAssetId as string | undefined) ?? null
                }
                externalUrl={
                  (lesson.config.externalUrl as string | undefined) ?? null
                }
                resumePositionSeconds={lesson.resumePositionSeconds ?? 0}
                watchThresholdPercent={threshold}
                onWatchedPercentChange={(percent, position) => {
                  setWatchedPercent(percent)
                  watchPositionRef.current = position
                  // Drives the transcript's active-line highlight.
                  setCurrentTimeMs(position * 1000)
                }}
                onPlayerReady={(element) => {
                  playerRef.current = element
                }}
                chapters={videoChapters}
              />
            </div>
          )}

          {/* Chapters, listed as well as marked on the scrubber (sc-1294).
            Markers alone are easy to miss, and a list is the thing people
            actually scan to decide where to start. */}
          {videoChapters.length > 0 && (
            <div className="mb-4 flex flex-wrap gap-1.5">
              {videoChapters.map((chapter) => (
                <button
                  key={chapter.id}
                  type="button"
                  onClick={() => {
                    const player = playerRef.current
                    if (!player) return
                    player.currentTime = chapter.startMs / 1000
                    void player.play().catch(() => undefined)
                  }}
                  className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors hover:bg-muted/60"
                >
                  <span className="text-muted-foreground tabular-nums">
                    {formatCueTime(chapter.startMs)}
                  </span>
                  {chapter.title}
                </button>
              ))}
            </div>
          )}

          {/* ── What you can do with this course ─────────────────────────
            Directly under the player, the way every course platform puts it.
            These act on the COURSE, not the lesson, which is why they sit
            above the lesson title rather than beside it. */}
          <div className="mb-4 flex flex-wrap items-center gap-1 border-b pb-3">
            <FavoriteButton
              courseId={course.courseId}
              courseSlug={courseSlug}
              isFavorite={course.isFavorite}
              variant="inline"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                void navigator.clipboard.writeText(window.location.href)
                toast.success('Link copied')
              }}
            >
              <Share2 className="size-4" aria-hidden />
              <span className="ml-1.5 text-xs font-medium tracking-wide uppercase">
                Share
              </span>
            </Button>
            {course.owner && (
              <Button variant="ghost" size="sm" asChild>
                <a
                  href={`mailto:${course.owner.email}?subject=${encodeURIComponent(
                    `${course.title} — ${lesson.title}`,
                  )}`}
                >
                  <Mail className="size-4" aria-hidden />
                  <span className="ml-1.5 text-xs font-medium tracking-wide uppercase">
                    Contact owner
                  </span>
                </a>
              </Button>
            )}
          </div>

          {/* ── Title, and the two things you do next ────────────────────────
            Finish and Next live HERE, level with the title, not at the foot of
            the page. Below the content they were a scroll away from the video
            that unlocks them, which reads as though the lesson has no end. */}
          <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold tracking-tight">
                  {lesson.title}
                </h1>
                <Badge variant="outline" className="text-[10px]">
                  {lessonTypeLabel(lesson.type)}
                </Badge>
                {isDone && (
                  <Badge
                    variant="outline"
                    className="border-emerald-500/30 bg-emerald-500/10 text-[10px] text-emerald-700 dark:text-emerald-400"
                  >
                    Complete
                  </Badge>
                )}
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                {course.owner && (
                  <span className="inline-flex items-center gap-1.5">
                    <Avatar className="size-5">
                      <AvatarFallback className="text-[9px]">
                        {`${course.owner.firstName.charAt(0)}${course.owner.lastName.charAt(0)}`.toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    {course.owner.firstName} {course.owner.lastName}
                  </span>
                )}
                {lesson.estimatedMinutes != null && (
                  <span className="inline-flex items-center gap-1">
                    <Clock className="size-3" aria-hidden />
                    {lesson.estimatedMinutes} min
                  </span>
                )}
                <span>{completionRuleLabel(lesson.completionRule)}</span>
              </p>
            </div>

            {/* A document lesson finishes by signing, so it gets no Finish
              button — two ways to complete the same thing would leave one of
              them writing a completion with no acknowledgment behind it. */}
            <div className="flex shrink-0 items-center gap-2">
              {lesson.type !== 'DOCUMENT_ACK' && !isDone && (
                <Button
                  disabled={Boolean(blockedReason) || complete.isPending}
                  title={blockedReason ?? undefined}
                  onClick={async () => {
                    // Flush watch progress BEFORE asking to complete.
                    //
                    // The server checks the recorded percent, not the one the
                    // browser claims — correctly, since a client could otherwise
                    // assert it had watched anything. But that makes the order
                    // load-bearing: reporting is throttled, so without this the
                    // last few seconds of watching are still in the browser and
                    // Finish is judged against a stale row. That is what made a
                    // fully watched video fail with "you are at 88%".
                    if (rule.kind === 'WATCH_THRESHOLD' && watchedPercent > 0) {
                      // Only this sitting's number is worth sending — the
                      // server already holds the rest and only moves forward.
                      try {
                        await reportProgress.mutateAsync({
                          lessonId: lesson.id,
                          watchedPercent,
                          resumePositionSeconds: Math.floor(
                            watchPositionRef.current,
                          ),
                        })
                      } catch {
                        // Let the completion attempt speak for itself rather
                        // than failing here with a second, less useful error.
                      }
                    }

                    complete.mutate(
                      {
                        lessonId: lesson.id,
                        scrolledToEnd: rule.kind === 'SCROLL_ACK' || undefined,
                        // The server re-checks against the recorded attempt;
                        // this only says which gate the learner cleared.
                        passed: rule.kind === 'QUIZ' ? quizPassed : undefined,
                      },
                      { onSuccess: goNext },
                    )
                  }}
                >
                  {complete.isPending && (
                    <Loader2 className="mr-1 size-4 animate-spin" aria-hidden />
                  )}
                  Finish lesson
                </Button>
              )}
              {isDone && (
                <Button onClick={goNext}>
                  {nextLesson ? 'Next lesson' : 'Back to the course'}
                  <ArrowRight className="ml-1 size-4" aria-hidden />
                </Button>
              )}
              {!isDone && nextLesson && (
                <Button variant="outline" onClick={goNext}>
                  Skip
                  <ArrowRight className="ml-1 size-4" aria-hidden />
                </Button>
              )}
            </div>
          </div>

          {/* Says what is still missing, next to the button that is disabled
            because of it — not buried under the fold. */}
          {blockedReason && !isDone && (
            <p className="mb-4 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
              {blockedReason}
            </p>
          )}

          {/* No card around the body. A short write-up in a bordered box reads
            as an empty container with a sentence lost in it. */}
          {lesson.type === 'CONTENT' && bodyMarkdown && (
            <div className="mb-6">
              <Markdown content={bodyMarkdown} />
            </div>
          )}

          {lesson.type === 'QUIZ' && (
            <div className="mb-6">
              <QuizLesson
                lessonId={lesson.id}
                courseSlug={courseSlug}
                questions={
                  (lesson.config.questions as
                    | Array<QuizQuestion>
                    | undefined) ?? []
                }
                passPercent={rule.kind === 'QUIZ' ? rule.passPercent : 100}
                alreadyPassed={isDone}
                onPassed={() => setQuizPassed(true)}
              />
            </div>
          )}

          {lesson.type === 'DOCUMENT_ACK' && (
            <div className="mb-6">
              <DocumentAcknowledgment
                lessonId={lesson.id}
                documentAssetId={
                  (lesson.config.documentAssetId as string | undefined) ?? ''
                }
                courseSlug={courseSlug}
                alreadySigned={isDone}
                onSigned={goNext}
              />
            </div>
          )}

          {/* Resources and discussion as tabs rather than stacked cards. Both
            sit above the finish button: a handout somebody was meant to
            download, or a question they wanted to ask, is no use discovered
            after they have already moved on. */}
          <div className="border-t pt-5">
            {/* Files first when there are any, then the transcript when the
              lesson has one — both are about the lesson itself, where
              discussion is about the people taking it. */}
            <Tabs
              defaultValue={
                lessonAttachments.length > 0
                  ? 'files'
                  : videoAssetId
                    ? 'transcript'
                    : 'discussion'
              }
            >
              <TabsList className="w-full">
                <TabsTrigger value="files" className="flex-1">
                  Attached files
                  {lessonAttachments.length > 0 && (
                    <span className="ml-1.5 text-xs text-muted-foreground tabular-nums">
                      {lessonAttachments.length}
                    </span>
                  )}
                </TabsTrigger>
                {videoAssetId && (
                  <TabsTrigger value="transcript" className="flex-1">
                    Transcript
                  </TabsTrigger>
                )}
                <TabsTrigger value="discussion" className="flex-1">
                  Discussion
                </TabsTrigger>
              </TabsList>

              {/* TabsContents, not bare TabsContent siblings. Without the
                wrapper the panel snaps from the discussion composer's height
                to a one-line empty state, and the whole page jumps. This
                animates the height, so only the section appears to change. */}
              <TabsContents>
                <TabsContent value="files" className="pt-4">
                  {lessonAttachments.length === 0 ? (
                    <p className="flex min-h-[8rem] items-center justify-center text-center text-sm text-muted-foreground">
                      Nothing attached to this lesson.
                    </p>
                  ) : (
                    <>
                      <AttachmentsHeading count={lessonAttachments.length} />
                      <AttachmentList attachments={lessonAttachments} />
                    </>
                  )}
                </TabsContent>

                {videoAssetId && (
                  <TabsContent value="transcript" className="pt-4">
                    <LessonTranscript
                      assetId={videoAssetId}
                      currentTimeMs={currentTimeMs}
                      onSeek={(ms) => {
                        const player = playerRef.current
                        if (!player) return
                        player.currentTime = ms / 1000
                        // Seeking is deliberately NOT credited as watching —
                        // MuxVideo's seek guard sees the jump and refuses it, which
                        // is what stops a transcript link near the end completing
                        // a lesson in one tick.
                        void player.play().catch(() => undefined)
                      }}
                    />
                  </TabsContent>
                )}

                <TabsContent value="discussion" className="pt-4">
                  {me?.id ? (
                    <DiscussionPanel
                      courseId={course.courseId}
                      lessonLineageId={lesson.lineageId}
                      currentUserId={me.id}
                      canModerate={canModerate}
                    />
                  ) : (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      Loading…
                    </p>
                  )}
                </TabsContent>
              </TabsContents>
            </Tabs>
          </div>
        </div>

        {/* ── Where you are ──────────────────────────────────────────────── */}
        {/* One card per chapter rather than one card holding everything: a
            long course was a single tall list with no landmarks, and the
            chapter is the landmark. */}
        <aside className="space-y-3 lg:sticky lg:top-4 lg:self-start">
          <ol className="space-y-3">
            {groupLessonsByChapter(course.chapters, course.lessons).map(
              (group) => (
                <li key={group.chapter?.id ?? 'ungrouped'}>
                  <Card>
                    <CardContent className="py-3">
                      <div className="mb-2 flex items-baseline justify-between gap-2 px-1">
                        <h3 className="text-sm font-semibold">
                          {group.chapter?.title ?? 'In this course'}
                        </h3>
                        {chapterMinutes(group.lessons) != null && (
                          <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
                            {chapterMinutes(group.lessons)} min
                          </span>
                        )}
                      </div>
                      <ol className="-mx-1 space-y-0.5">
                        {group.lessons.map((item) => {
                          const itemIndex = course.lessons.indexOf(item)
                          const isCurrent = item.id === lesson.id
                          const done = Boolean(item.completedAt)
                          return (
                            <li key={item.id}>
                              <Link
                                to="/learning/$courseSlug/lesson/$lessonId"
                                params={{ courseSlug, lessonId: item.id }}
                                className={cn(
                                  'flex items-start gap-2.5 rounded-md px-2 py-2 text-sm transition-colors',
                                  isCurrent
                                    ? 'bg-primary/10 font-medium text-primary'
                                    : 'hover:bg-muted/60',
                                )}
                                aria-current={isCurrent ? 'step' : undefined}
                              >
                                {done ? (
                                  <CheckCircle2
                                    className="mt-0.5 size-4 shrink-0 text-emerald-600 dark:text-emerald-400"
                                    aria-label="Completed"
                                  />
                                ) : (
                                  <Circle
                                    className="mt-0.5 size-4 shrink-0 text-muted-foreground/50"
                                    aria-hidden
                                  />
                                )}
                                <span className="min-w-0 flex-1">
                                  <span className="text-xs text-muted-foreground tabular-nums">
                                    {itemIndex + 1}.{' '}
                                  </span>
                                  {item.title}
                                </span>
                                {/* The duration was in the data all along and simply
                            never rendered — the single most-missed fact when
                            deciding whether to start the next one now. */}
                                {item.estimatedMinutes != null && (
                                  <span className="mt-0.5 shrink-0 text-[11px] text-muted-foreground tabular-nums">
                                    {item.estimatedMinutes}m
                                  </span>
                                )}
                              </Link>
                            </li>
                          )
                        })}
                      </ol>
                    </CardContent>
                  </Card>
                </li>
              ),
            )}
          </ol>
        </aside>
      </div>
    </div>
  )
}
